// pages/TradeIn/MyTradeIns.jsx
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTheme } from '../../components/Theme/ThemeProvider';
import { summaryApi } from '../../common';
import { toast } from 'react-toastify';
import { 
  ArrowLeft, Package, Edit, Trash2, Clock, 
  CheckCircle, XCircle, Eye 
} from 'lucide-react';
import { authFetch } from '../../helpers/authFetch';

function MyTradeIns() {
  const { colors } = useTheme();
  const navigate = useNavigate();
  
  const [tradeIns, setTradeIns] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTradeIns = async () => {
    try {
      setLoading(true);
      const response = await authFetch(summaryApi.getMyTradeIns.url, {
        method: summaryApi.getMyTradeIns.method,
        
      });

      const data = await response.json();

      if (data.success) {
        setTradeIns(data.data);
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Failed to fetch trade-ins');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (tradeInId) => {
    if (!window.confirm('Are you sure you want to delete this trade-in request?')) return;

    try {
      const response = await authFetch(`${summaryApi.deleteTradeIn.url}/${tradeInId}`, {
        method: summaryApi.deleteTradeIn.method,
        
      });

      const data = await response.json();

      if (data.success) {
        toast.success('Trade-in deleted successfully');
        fetchTradeIns();
      } else {
        toast.error(data.message);
      }
    } catch (error) {
      toast.error('Failed to delete trade-in');
    }
  };

  useEffect(() => {
    fetchTradeIns();
  }, []);

  const getStatusConfig = (status) => {
    const configs = {
      Pending: { color: '#F59E0B', icon: Clock, label: 'Pending Review' },
      'Under Review': { color: '#3B82F6', icon: Eye, label: 'Under Review' },
      Accepted: { color: '#10B981', icon: CheckCircle, label: 'Accepted' },
      Rejected: { color: '#EF4444', icon: XCircle, label: 'Rejected' },
      Completed: { color: '#8B5CF6', icon: CheckCircle, label: 'Completed' }
    };
    return configs[status] || configs.Pending;
  };

  return (
    <div className="min-h-screen pt-24 pb-16" style={{ backgroundColor: colors.bg }}>
      <div className="max-w-6xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate(-1)}
              className="p-2 rounded-lg transition-all hover:scale-110"
              style={{
                backgroundColor: colors.bgSecondary,
                color: colors.primary
              }}
            >
              <ArrowLeft size={24} />
            </button>
            <div>
              <h1 className="text-4xl font-bold" style={{ color: colors.text }}>
                My Trade-Ins
              </h1>
              <p style={{ color: colors.textSecondary }}>
                {tradeIns.length} trade-in requests
              </p>
            </div>
          </div>

          <Link
            to="/submit-tradein"
            className="px-6 py-3 rounded-lg font-semibold text-white transition-all hover:scale-105"
            style={{
              background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
            }}
          >
            + New Trade-In
          </Link>
        </div>

        {/* Trade-Ins Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-transparent" style={{ borderTopColor: colors.primary }} />
          </div>
        ) : tradeIns.length === 0 ? (
          <div
            className="text-center py-16 rounded-xl"
            style={{ backgroundColor: colors.bgSecondary }}
          >
            <Package size={48} className="mx-auto mb-4" style={{ color: colors.textSecondary, opacity: 0.3 }} />
            <p style={{ color: colors.textSecondary }} className="text-lg mb-4">
              No trade-in requests yet
            </p>
            <Link
              to="/submit-tradein"
              className="inline-block px-6 py-3 rounded-lg font-semibold text-white transition-all hover:scale-105"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
              }}
            >
              Submit Your First Trade-In
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {tradeIns.map((tradeIn) => {
              const statusConfig = getStatusConfig(tradeIn.status);
              const StatusIcon = statusConfig.icon;
              
              return (
                <div
                  key={tradeIn._id}
                  className="rounded-xl overflow-hidden transition-all hover:shadow-lg"
                  style={{
                    backgroundColor: colors.bgSecondary,
                    border: `1px solid ${colors.border}`
                  }}
                >
                  {/* Image */}
                  <div className="relative h-48">
                    <img
                      src={tradeIn.phoneImages[0]}
                      alt={`${tradeIn.phoneBrand} ${tradeIn.phoneModel}`}
                      className="w-full h-full object-cover"
                    />
                    <div
                      className="absolute top-3 right-3 px-3 py-1 rounded-full text-xs font-bold flex items-center gap-1"
                      style={{ backgroundColor: `${statusConfig.color}20`, color: statusConfig.color }}
                    >
                      <StatusIcon size={14} />
                      {statusConfig.label}
                    </div>
                  </div>

                  {/* Content */}
                  <div className="p-4 space-y-3">
                    <h3 className="font-bold text-lg" style={{ color: colors.text }}>
                      {tradeIn.phoneBrand} {tradeIn.phoneModel}
                    </h3>

                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div>
                        <span style={{ color: colors.textSecondary }}>Storage:</span>
                        <span className="ml-2 font-semibold" style={{ color: colors.text }}>
                          {tradeIn.storage}
                        </span>
                      </div>
                      <div>
                        <span style={{ color: colors.textSecondary }}>Condition:</span>
                        <span className="ml-2 font-semibold" style={{ color: colors.text }}>
                          {tradeIn.phoneCondition}
                        </span>
                      </div>
                    </div>

                    {tradeIn.offeredValue && (
                      <div
                        className="p-3 rounded-lg"
                        style={{ backgroundColor: colors.primaryLight }}
                      >
                        <p className="text-sm" style={{ color: colors.textSecondary }}>
                          Offered Value
                        </p>
                        <p className="text-2xl font-bold" style={{ color: colors.primary }}>
                          ₦{tradeIn.offeredValue.toLocaleString()}
                        </p>
                      </div>
                    )}

                    {tradeIn.adminNotes && (
                      <div
                        className="p-3 rounded-lg text-sm"
                        style={{ backgroundColor: colors.bg }}
                      >
                        <p className="font-semibold mb-1" style={{ color: colors.text }}>
                          Admin Notes:
                        </p>
                        <p style={{ color: colors.textSecondary }}>{tradeIn.adminNotes}</p>
                      </div>
                    )}

                    {tradeIn.rejectionReason && (
                      <div
                        className="p-3 rounded-lg text-sm"
                        style={{ backgroundColor: '#EF444420' }}
                      >
                        <p className="font-semibold mb-1" style={{ color: '#EF4444' }}>
                          Rejection Reason:
                        </p>
                        <p style={{ color: colors.text }}>{tradeIn.rejectionReason}</p>
                      </div>
                    )}

                    <p className="text-xs" style={{ color: colors.textSecondary }}>
                      Submitted {new Date(tradeIn.createdAt).toLocaleDateString()}
                    </p>

                    {/* Actions */}
                    {tradeIn.status === 'Pending' && (
                      <div className="flex gap-2 pt-2">
                        <Link
                          to={`/edit-tradein/${tradeIn._id}`}
                          className="flex-1 py-2 rounded-lg font-semibold transition-all hover:scale-105 flex items-center justify-center gap-2"
                          style={{
                            backgroundColor: colors.primaryLight,
                            color: colors.primary
                          }}
                        >
                          <Edit size={16} />
                          Edit
                        </Link>
                        <button
                          onClick={() => handleDelete(tradeIn._id)}
                          className="flex-1 py-2 rounded-lg font-semibold transition-all hover:scale-105 flex items-center justify-center gap-2"
                          style={{
                            backgroundColor: '#EF444420',
                            color: '#EF4444'
                          }}
                        >
                          <Trash2 size={16} />
                          Delete
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default MyTradeIns;